import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/app/admin/_common/ui/dialog";
import { Sheet, SheetContent, SheetTitle } from "@/app/admin/_common/ui/sheet";
import CuoButton from "@/app/admin/_common/CuoButton";
import { PerformerForm } from "./PerformerForm";
import { usePerformerFormFlow } from "./usePerformerFormFlow";
import type { Performer } from "@/app/_services/PerformerService";
import { Drama, X } from "lucide-react";
import { useIsMobile } from "@/app/admin/_hooks/useIsMobile";

export const PerformerDialog = ({
  defaultPerformer,
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  defaultPerformer?: Performer | null;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const flow = usePerformerFormFlow({
    defaultPerformer,
    onSaved: () => {
      onSaved();
      onClose();
    },
  });
  const { control, errors, isSaving, handleSave, setSelectedImage } = flow;
  const isMobile = useIsMobile();
  const title = defaultPerformer
    ? `${defaultPerformer.first_name} ${defaultPerformer.last_name}`
    : "New Performer";

  const form = (
    <PerformerForm
      control={control}
      errors={errors}
      defaultPerformer={defaultPerformer}
      onImageSelect={setSelectedImage}
    />
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
        <SheetContent
          side="right"
          hideClose
          className="flex h-full w-screen max-w-none flex-col gap-0 border-l-0 bg-white p-0 sm:max-w-none"
        >
          <SheetTitle className="sr-only">{title}</SheetTitle>
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <span className="inline-flex items-center gap-2 text-[13px] font-normal text-foreground">
              <Drama className="size-4 flex-none text-muted-foreground" />
              {title}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
              aria-label="Close"
            >
              <X className="size-3.5" />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
            <p className="text-[12.5px] text-muted-foreground">
              Enter the performer's name and contact info.
            </p>
            {form}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-border p-3">
            <CuoButton variant="outline" label="Cancel" onClick={onClose} />
            <CuoButton disableOnAsync={isSaving} variant="default" onClick={handleSave} label="Save" />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="inset-0 left-0 top-0 m-auto h-fit max-h-[85vh] w-full max-w-[440px] translate-x-0 translate-y-0 bg-white">
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2 align-middle">
              <Drama className="size-4 flex-none text-muted-foreground" />
              {title}
            </span>
          </DialogTitle>
          <DialogDescription>Enter the performer's name and contact info.</DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto py-2">{form}</div>

        <DialogFooter>
          <CuoButton variant="outline" label="Cancel" onClick={onClose} />
          <CuoButton disableOnAsync={isSaving} variant="default" onClick={handleSave} label="Save" />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
